/**
 * collectionGroup クエリのビルダー関数。
 *
 * manage / web の React hooks と integration-test の両方から利用可能。
 * hooks 側は返された Query を useObservable 等でサブスクライブし、
 * テスト側は getDocs() で直接実行する。
 */
import * as FS from "firebase/firestore"
import { FirestorePaths, UserId } from "popopo-msg"

/** useFollowingUsers (manage: フォロー中一覧) */
export function followingUsersQuery(firestore: FS.Firestore, userId: UserId, limit?: number): FS.Query {
  return FS.query(
    FS.collectionGroup(firestore, FirestorePaths.Users.Followers.path),
    FS.where("user_id", "==", userId),
    FS.orderBy("followee.name", "asc"),
    ...(limit != null ? [FS.limit(limit)] : []),
  )
}

/** useJoiningSpaces (manage: 所属スペース一覧) */
export function joiningSpacesQuery(firestore: FS.Firestore, userId: UserId, limit?: number): FS.Query {
  return FS.query(
    FS.collectionGroup(firestore, FirestorePaths.Spaces.SpaceUsers.path),
    FS.where("user_id", "==", userId),
    ...(limit != null ? [FS.limit(limit)] : []),
  )
}

/** useLiveViewersByUser (manage: ライブ視聴履歴) */
export function liveViewersByUserQuery(firestore: FS.Firestore, userId: UserId): FS.Query {
  return FS.query(
    FS.collectionGroup(firestore, FirestorePaths.Spaces.Lives.LiveViewers.path),
    FS.where("user_id", "==", userId),
    FS.orderBy("updated_at", "desc"),
  )
}

/** useLiveCommentsByUser / useLiveCommentSearch (manage: LIVEコメント) */
export function liveCommentsByUserQuery(firestore: FS.Firestore, userId: UserId): FS.Query {
  return FS.query(
    FS.collectionGroup(firestore, FirestorePaths.Spaces.Lives.LiveComments.path),
    FS.where("user.id", "==", userId),
    FS.orderBy("created_at", "desc"),
  )
}

/** useSpaceMessagesByUser (manage: スペースメッセージ) */
export function spaceMessagesByUserQuery(firestore: FS.Firestore, userId: UserId): FS.Query {
  return FS.query(
    FS.collectionGroup(firestore, FirestorePaths.Spaces.SpaceMessages.path),
    FS.where("user.id", "==", userId),
    FS.orderBy("created_at", "desc"),
  )
}

/** useLivePowerHistoriesByUser (manage: パワー送信履歴) */
export function livePowerHistoriesByUserQuery(firestore: FS.Firestore, userId: UserId): FS.Query {
  return FS.query(
    FS.collectionGroup(firestore, FirestorePaths.LiveHistories.LivePowerHistories.path),
    FS.where("user.id", "==", userId),
    FS.orderBy("created_at", "desc"),
  )
}

/** useSelectionsByUser (manage: 抽選一覧) */
export function selectionsByUserQuery(firestore: FS.Firestore, userId: UserId): FS.Query {
  return FS.query(
    FS.collectionGroup(firestore, FirestorePaths.Spaces.Lives.Selections.path),
    FS.where("user_id", "==", userId),
    FS.orderBy("created_at", "desc"),
  )
}

/** useSelectionParticipantSearch (manage: 抽選参加者検索 — ユーザーID指定時) */
export function selectionParticipantSearchByUserQuery(firestore: FS.Firestore, userId: UserId): FS.Query {
  return FS.query(
    FS.collectionGroup(firestore, FirestorePaths.Spaces.Lives.Selections.SelectionParticipants.path),
    FS.where("user.id", "==", userId),
    FS.orderBy("created_at", "desc"),
  )
}

/** useGlobalVoices (web: ボイス一覧) */
export function globalVoicesQuery(firestore: FS.Firestore, limit?: number): FS.Query {
  return FS.query(
    FS.collectionGroup(firestore, FirestorePaths.Users.Voices.path),
    FS.orderBy("created_at", "desc"),
    ...(limit != null ? [FS.limit(limit)] : []),
  )
}

/** useAllItemDistributionsForAdmin / fetchAllItemDistributionsForAdmin (manage: アイテム販売一覧) */
export function allItemDistributionsQuery(firestore: FS.Firestore): FS.Query {
  return FS.query(
    FS.collectionGroup(firestore, FirestorePaths.Items.Distributions.path),
  )
}
